import {
  Box,
  Button,
  Chip,
  FormHelperText,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { useEffect, useRef } from 'react';
import SqsIcon from '../../../../assets/aws/sqs.svg';
import type { SqsQueue } from 'config-editor-common';

const SQS_COLOR = '#E7157B';
const SQS_COLOR_LIGHT = '#F4A8C8';

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(2),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  icon: {
    width: '1.5rem',
    height: '1.5rem',
  },
  queueItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `2px solid ${SQS_COLOR_LIGHT}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    position: 'relative',
    '&:hover': {
      boxShadow: theme.shadows[2],
      borderColor: SQS_COLOR,
    },
  },
  queueHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  nameContainer: {
    flex: 1,
    minWidth: 0,
    marginRight: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  queueIcon: {
    width: '1.25rem',
    height: '1.25rem',
    flexShrink: 0,
    display: 'block',
  },
  consumerChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
}));

interface ValkyrieSqsQueueListEditorProps {
  queues: SqsQueue[];
  onChange: (queues: SqsQueue[]) => void;
  /** Map of lambda name -> queue name for showing which lambdas consume each queue */
  lambdaQueueMap?: Record<string, string>;
}

const DEFAULT_QUEUE: SqsQueue = {
  delay_seconds: 0,
  receive_wait_time_seconds: 10,
  visibility_timeout_seconds: 1800,
  max_receive_count: 3,
};

/** `parseInt(...) || fallback` swallows a legitimate 0, so NaN is the only thing that falls back. */
function parseNumber(value: string, fallback: number): number {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
}

function getConsumersForQueue(queueName: string, lambdaQueueMap: Record<string, string>): string[] {
  return Object.entries(lambdaQueueMap)
    .filter(([, q]) => q === queueName)
    .map(([lambda]) => lambda);
}

export const ValkyrieSqsQueueListEditor = ({
  queues,
  onChange,
  lambdaQueueMap = {},
}: ValkyrieSqsQueueListEditorProps) => {
  const classes = useStyles();
  const newlyAddedIndexRef = useRef<number | null>(null);
  const queueRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const nameInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const updateQueue = (index: number, field: keyof SqsQueue, value: unknown) => {
    const newQueues = [...queues];
    newQueues[index] = { ...newQueues[index], [field]: value };
    onChange(newQueues);
  };

  const addQueue = () => {
    const newIndex = queues.length;
    newlyAddedIndexRef.current = newIndex;
    onChange([...queues, { ...DEFAULT_QUEUE }]);
  };

  // Scroll to newly added queue and focus its name input (like phx-api-v2 LambdaListEditor)
  useEffect(() => {
    const index = newlyAddedIndexRef.current;
    if (index !== null) {
      const element = queueRefs.current[index];
      const nameInput = nameInputRefs.current[index];
      if (element && nameInput) {
        requestAnimationFrame(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          nameInput.focus();
          nameInput.select();
          newlyAddedIndexRef.current = null;
        });
      }
    }
  }, [queues]);

  const removeQueue = (index: number) => {
    onChange(queues.filter((_, i) => i !== index));
  };

  return (
    <div className={classes.section}>
      <Box className={classes.header}>
        <Box>
          <div className={classes.titleContainer}>
            <img src={SqsIcon} alt="SQS" className={classes.icon} />
            <Typography variant="h6">SQS Queues</Typography>
          </div>
          <FormHelperText>
            Configure queues for async processing.{' '}
            <strong>An additional queue suffixed with -dlq will be created for each queue</strong>{' '}
            to receive failed messages.
          </FormHelperText>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={addQueue}
          startIcon={<AddIcon />}
          style={{ backgroundColor: SQS_COLOR, color: '#FFFFFF' }}
        >
          Add Queue
        </Button>
      </Box>

      {queues.length === 0 ? (
        <div className={classes.emptyState}>
          <Typography variant="body1">No SQS queues configured</Typography>
          <Typography variant="body2" style={{ marginTop: 8 }}>
            Add a queue to enable async message processing between your endpoint lambdas and queue
            consumer lambdas.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addQueue}
            style={{
              marginTop: 16,
              backgroundColor: SQS_COLOR,
              color: '#FFFFFF',
            }}
          >
            Add First Queue
          </Button>
        </div>
      ) : (
        queues.map((queue, index) => {
          const queueName = queue.name || '';
          const consumers = getConsumersForQueue(queueName, lambdaQueueMap);

          return (
            <div
              key={index}
              ref={el => {
                queueRefs.current[index] = el;
              }}
            >
              <Paper variant="outlined" className={classes.queueItem}>
                <div className={classes.queueHeader}>
                  <div className={classes.nameContainer}>
                    <img src={SqsIcon} alt="SQS" className={classes.queueIcon} />
                    <TextField
                      inputRef={el => {
                        nameInputRefs.current[index] = el;
                      }}
                      label="Queue Name"
                      value={queue.name || ''}
                      onChange={e => updateQueue(index, 'name', e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="e.g., performance_queue"
                    />
                  </div>
                  <Tooltip title="Delete Queue">
                    <IconButton
                      size="small"
                      onClick={() => removeQueue(index)}
                      aria-label="Remove queue"
                      color="secondary"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </div>

                {consumers.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="caption" color="textSecondary">
                      Consumed by:
                    </Typography>
                    <div className={classes.consumerChips}>
                      {consumers.map(lambda => (
                        <Chip key={lambda} label={lambda} size="small" variant="outlined" />
                      ))}
                    </div>
                  </Box>
                )}

                <Grid container spacing={2} style={{ marginTop: 8 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Visibility timeout"
                      type="number"
                      value={queue.visibility_timeout_seconds ?? 1800}
                      onChange={e =>
                        updateQueue(
                          index,
                          'visibility_timeout_seconds',
                          parseNumber(e.target.value, 1800),
                        )
                      }
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0, max: 43200 }}
                      helperText="Seconds. Between 0 seconds and 12 hours."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Delivery delay"
                      type="number"
                      value={queue.delay_seconds ?? 0}
                      onChange={e =>
                        updateQueue(index, 'delay_seconds', parseNumber(e.target.value, 0))
                      }
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0, max: 900 }}
                      helperText="Seconds. Between 0 seconds and 15 minutes."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Receive message wait time"
                      type="number"
                      value={queue.receive_wait_time_seconds ?? 10}
                      onChange={e =>
                        updateQueue(
                          index,
                          'receive_wait_time_seconds',
                          parseNumber(e.target.value, 10),
                        )
                      }
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0, max: 20 }}
                      helperText="Seconds. Long polling wait time."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Maximum receives"
                      type="number"
                      value={queue.max_receive_count ?? 3}
                      onChange={e =>
                        updateQueue(index, 'max_receive_count', parseInt(e.target.value, 10) || 3)
                      }
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 1, max: 1000 }}
                      helperText="Retries before moving to dead-letter queue."
                    />
                  </Grid>
                </Grid>
              </Paper>
            </div>
          );
        })
      )}
    </div>
  );
};
